// PlateOverlayLayer.tsx — MapLibre raster overlay for FAA d-TPP approach plates.
//
// Coordinator component. Delegates each concern to focused sub-modules:
//
//   resolveCorners      — /api/plates/georef resolution + CIFP-bbox fallback
//   renderPlateBitmap   — PDF.js rasterization (HI-5 memory cap preserved)
//   imageSource         — MapLibre image-source mount/teardown/blob URL lifecycle
//   ownshipOverlay      — ownship triangle on the plate
//   routeOverlay        — flight-plan route on the plate
//
// Per audit synthesis 2026-05-24 § HI-3':
//   Phase 1 — ~400 LOC of confirmed dead synthetic-geometry code removed:
//     addProcedureHoldsToMap / addHoldEntriesToMap / addProcedurePtsToMap
//     (never called; only their remove* counterparts ran inside cleanup)
//     extractCifpHoldLegs / extractCifpPtLegs (zero importers in repo)
//     HOLDS_SOURCE_ID / HOLD_ENTRIES_SOURCE_ID / PTS_SOURCE_ID + *_LAYER_IDs
//     Author note at former line 1257: "plate-overlay no longer renders holds
//     or procedure turns synthetically. The plate raster already shows them."
//     PlanProceduresLayer is the single source of truth for hold + PT rendering.
//
//   Phase 2 — live path split into 5 focused modules (see above).
//
// Design doc: data/plate-overlay-research-2026-05-14.md

import { useEffect, useRef } from 'react';
import { useUiStore } from '../stores/uiStore';
import { useAuthStore } from '../stores/authStore';
import { useGpsStore } from '../stores/gpsStore';
import { usePlanStore } from '../stores/planStore';
import { getMapInstance } from './MapView';
import { shouldUpdateOwnshipOnPlate } from './plateOwnshipGate';
import { resolveCorners } from './plateOverlay/resolveCorners';
import { renderPlanViewBitmap } from './plateOverlay/renderPlateBitmap';
import {
  LAYER_ID,
  addOverlayToMap,
  removeOverlayFromMap,
} from './plateOverlay/imageSource';
import {
  addPlateOwnshipToMap,
  removePlateOwnshipFromMap,
} from './plateOverlay/ownshipOverlay';
import {
  addPlateRouteToMap,
  removePlateRouteFromMap,
} from './plateOverlay/routeOverlay';
import type { PlateBboxCorners } from '../lib/plateGeoref';

// ── Full overlay teardown ────────────────────────────────────────────────────

function teardownAllLayers(): void {
  removeOverlayFromMap();
  removePlateOwnshipFromMap();
  removePlateRouteFromMap();
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PlateOverlayLayer() {
  const activePlateOverlay = useUiStore((s) => s.activePlateOverlay);
  const plateOverlayOpacity = useUiStore((s) => s.plateOverlayOpacity);
  const plateOverlayShowFullPage = useUiStore((s) => s.plateOverlayShowFullPage);
  const plateOverlayShowOwnship = useUiStore((s) => s.plateOverlayShowOwnship);
  const plateOverlayResult = useUiStore((s) => s.plateOverlayResult);

  const lastRenderedPdfName = useRef<string | null>(null);
  const lastRenderedFullPage = useRef<boolean>(false);
  const abortRef = useRef<AbortController | null>(null);
  // Track last GPS state we rendered so we can gate redundant updates.
  const lastGpsFix = useRef<{ lat: number; lon: number; hdg: number } | null>(null);

  // ── Effect: render plate when activePlateOverlay changes ──────────────────
  useEffect(() => {
    if (!activePlateOverlay) {
      abortRef.current?.abort();
      abortRef.current = null;
      lastRenderedPdfName.current = null;
      teardownAllLayers();
      useUiStore.getState().setPlateOverlayResult(null);
      return;
    }

    // Skip if both pdf and full-page mode are unchanged. Toggling full-page
    // forces a re-render so the bitmap crop + corners stay matched.
    if (
      lastRenderedPdfName.current === activePlateOverlay.pdfName &&
      lastRenderedFullPage.current === plateOverlayShowFullPage
    ) return;

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    const spec = activePlateOverlay;
    const pdfUrl = `/api/plates/pdf?cycle=${encodeURIComponent(spec.cycle)}&pdf=${encodeURIComponent(spec.pdfName)}`;
    // Phase 3: read session token once at effect-call time so submitters
    // see their own pending corrections on the chart.
    const sessionToken = useAuthStore.getState().session?.access_token;

    (async () => {
      // Resolve corners FIRST. The server tells us the exact page-
      // normalized rectangle to crop ('planViewRect'); cropping the
      // bitmap to a different rect would smash/stretch the image
      // onto the rectangular geo quad.
      let resolved;
      try {
        resolved = await resolveCorners(spec, ac.signal, sessionToken);
      } catch (err: unknown) {
        if ((err as { name?: string }).name === 'AbortError') return;
        console.warn('[PlateOverlayLayer] corner resolution failed', err);
        return;
      }
      if (ac.signal.aborted) return;

      // Publish the resolved status + residual to the store so the
      // PlateOverlayControls amber banner can read it.
      useUiStore.getState().setPlateOverlayResult({
        status: resolved.resultStatus,
        residualNm: resolved.residualNm,
      });

      // Full-page mode only works when the server georef supplied pageCorners.
      // CIFP-bbox fallback corners aren't accurate enough for full-page render.
      const useFullPage = plateOverlayShowFullPage && !!resolved.pageCorners;
      const targetCorners = useFullPage
        ? (resolved.pageCorners as PlateBboxCorners)
        : resolved.corners;

      let dataUrl: string;
      try {
        dataUrl = await renderPlanViewBitmap(
          pdfUrl,
          resolved.planViewFraction,
          resolved.planViewRect,
          ac.signal,
          useFullPage,
        );
      } catch (err: unknown) {
        if ((err as { name?: string }).name === 'AbortError') return;
        console.warn('[PlateOverlayLayer] PDF render failed', err);
        return;
      }
      if (ac.signal.aborted) return;

      lastRenderedPdfName.current = spec.pdfName;
      lastRenderedFullPage.current = useFullPage;
      const opacity = useUiStore.getState().plateOverlayOpacity;
      addOverlayToMap(targetCorners, dataUrl, opacity);

      // Yellow truth-dot rendering disabled by default — plate icons now
      // align well enough that the blue triangles from the fixes layer
      // (drawn ON TOP of the plate via beforeId='fixes-icon') serve as
      // sufficient ground-truth reference. Re-enable for debugging by
      // calling addFixMarkersToMap(resolved.matches) from imageSource.ts.

      // Procedure holds + turns are NOT rendered synthetically here.
      // 2026-05-15: the plate raster already shows them in their published
      // form; layering generated geometry on top was double-drawing and
      // risked misalignment when the raster depicts a hold slightly
      // differently than CIFP encodes it.
      // PlanProceduresLayer is the single source of truth for hold + PT +
      // entry rendering, driven by the active flight plan. It sits on top
      // of the plate raster (added LATER to the map stack).
    })().catch((err: unknown) => {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.warn('[PlateOverlayLayer] render failed', err);
    });

    return () => ac.abort();
  }, [activePlateOverlay, plateOverlayShowFullPage]);

  // ── Effect: sync opacity changes without re-rendering ────────────────────
  useEffect(() => {
    if (!activePlateOverlay) return;
    const map = getMapInstance();
    if (!map || !map.getLayer(LAYER_ID)) return;
    map.setPaintProperty(LAYER_ID, 'raster-opacity', plateOverlayOpacity);
  }, [plateOverlayOpacity, activePlateOverlay]);

  // ── Effect: ownship + route overlays on plate ────────────────────────────
  //
  // Active only when:
  //   - a plate overlay is active
  //   - the plate georef resolved as 'ok' (not low-confidence / fallback)
  //   - the user has the toggle ON (plateOverlayShowOwnship)
  //
  // GPS updates are gated: we only call setData when the fix has moved
  // >100 m OR heading changed >2° since the last update (avoids the
  // per-GPS-fix setData perf trap that the 2026-05-16 audit flagged).
  useEffect(() => {
    const overlayOk = activePlateOverlay && plateOverlayResult?.status === 'ok';
    if (!overlayOk || !plateOverlayShowOwnship) {
      removePlateOwnshipFromMap();
      removePlateRouteFromMap();
      lastGpsFix.current = null;
      return;
    }

    // Render the route immediately (plan rarely changes mid-flight).
    addPlateRouteToMap();

    // Render ownship from current GPS fix.
    const renderOwnship = (force = false) => {
      const fix = useGpsStore.getState().fix;
      if (!fix) return;
      const hdg = fix.headingDeg ?? 0;
      const prev = lastGpsFix.current;
      if (!force && prev) {
        if (!shouldUpdateOwnshipOnPlate(prev.lat, prev.lon, prev.hdg, fix.lat, fix.lon, hdg)) return;
      }
      lastGpsFix.current = { lat: fix.lat, lon: fix.lon, hdg };
      addPlateOwnshipToMap(fix.lat, fix.lon, hdg);
    };

    // Initial render.
    renderOwnship(true);

    // Subscribe to GPS store for subsequent fixes.
    const unsubGps = useGpsStore.subscribe(() => renderOwnship());

    // Subscribe to plan store so the route redraws if the plan changes.
    const unsubPlan = usePlanStore.subscribe(() => addPlateRouteToMap());

    return () => {
      unsubGps();
      unsubPlan();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlateOverlay, plateOverlayResult?.status, plateOverlayShowOwnship]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      teardownAllLayers();
    };
  }, []);

  return null;
}
